import React from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  formatRequirementValue,
  getDeletedOptions,
  isDeletedOption,
  RequirementValue
} from "../../utils/bookingRequirements";
import { RequirementType } from "../../types/VenueGroup";

// Radix Select does not support empty string values; this sentinel represents "no selection"
const NONE_SENTINEL = "__none__";

export interface BooleanFieldProps {
  id: string;
  label: string;
  value: boolean;
  onChange: (checked: boolean) => void;
}

export const BooleanField = ({
  id,
  label,
  value,
  onChange
}: BooleanFieldProps) => (
  <div className="flex items-center gap-2">
    <Checkbox
      id={id}
      checked={value}
      onCheckedChange={(checked) => onChange(checked === true)}
    />
    <Label htmlFor={id}>{label}</Label>
  </div>
);

export interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export const TextField = ({ id, label, value, onChange }: TextFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export interface LongtextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export const LongtextField = ({
  id,
  label,
  value,
  onChange
}: LongtextFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  placeholder?: string;
  onChange: (value: string) => void;
}

export const SelectField = ({
  id,
  label,
  value,
  options,
  placeholder = "Select an option",
  onChange
}: SelectFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Select
      value={value || NONE_SENTINEL}
      onValueChange={(newValue) =>
        onChange(newValue === NONE_SENTINEL ? "" : newValue)
      }
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_SENTINEL}>None</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export interface ReadOnlyFieldProps {
  label: string;
  value: RequirementValue;
  type: RequirementType;
  options?: string[];
}

export const ReadOnlyField = ({
  label,
  value,
  type,
  options
}: ReadOnlyFieldProps) => {
  const deletedOptions =
    type === "multiselect" && Array.isArray(value) && options
      ? getDeletedOptions(value, options)
      : [];

  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {type === "multiselect" && Array.isArray(value) && value.length > 0 ? (
        <span className="text-sm text-muted-foreground">
          {value.map((item, index) => {
            const isDeleted = deletedOptions.includes(item);
            return (
              <span key={item}>
                {index > 0 && ", "}
                <span className={isDeleted ? "line-through opacity-60" : ""}>
                  {item}
                  {isDeleted && <span className="sr-only"> (removed)</span>}
                </span>
              </span>
            );
          })}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">
          {formatRequirementValue(type, value ?? undefined) || "Not set"}
        </span>
      )}
    </div>
  );
};

export interface EditableFieldSwitchProps {
  fieldId: string;
  type: string;
  label: string;
  value: RequirementValue;
  options?: string[];
  onChange: (value: RequirementValue) => void;
  fallback?: React.ReactNode;
}

export const EditableFieldSwitch = ({
  fieldId,
  type,
  label,
  value,
  options = [],
  onChange,
  fallback = null
}: EditableFieldSwitchProps) => {
  switch (type) {
    case "boolean":
      return (
        <BooleanField
          id={fieldId}
          label={label}
          value={value === true || value === "true"}
          onChange={(checked) => onChange(checked)}
        />
      );
    case "text":
      return (
        <TextField
          id={fieldId}
          label={label}
          value={typeof value === "string" ? value : String(value ?? "")}
          onChange={(newValue) => onChange(newValue)}
        />
      );
    case "longtext":
      return (
        <LongtextField
          id={fieldId}
          label={label}
          value={typeof value === "string" ? value : String(value ?? "")}
          onChange={(newValue) => onChange(newValue)}
        />
      );
    case "select":
      return (
        <SelectField
          id={fieldId}
          label={label}
          value={typeof value === "string" ? value : String(value ?? "")}
          options={options}
          onChange={(newValue) => onChange(newValue)}
        />
      );
    case "multiselect":
      return (
        <MultiselectField
          fieldId={fieldId}
          label={label}
          value={Array.isArray(value) ? value : []}
          options={options}
          onChange={(updated) => onChange(updated)}
        />
      );
    default:
      return <>{fallback}</>;
  }
};

export interface MultiselectFieldProps {
  fieldId: string;
  label: string;
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
}

export const MultiselectField = ({
  fieldId,
  label,
  value,
  options: availableOptions,
  onChange
}: MultiselectFieldProps) => {
  const deletedOptions = getDeletedOptions(value, availableOptions);

  const handleToggle = (option: string) => {
    const isSelected = value.includes(option);

    if (!isSelected && isDeletedOption(option, availableOptions)) {
      return;
    }

    const updated = isSelected
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(updated);
  };

  return (
    <fieldset className="flex flex-col gap-1.5 border-none p-0 m-0">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="flex flex-col gap-1">
        {availableOptions.map((option) => {
          const optionId = `${fieldId}-${option}`;
          const isSelected = value.includes(option);

          return (
            <label
              key={option}
              htmlFor={optionId}
              className="flex items-center gap-2 py-1.5 text-sm cursor-pointer"
            >
              <Checkbox
                id={optionId}
                checked={isSelected}
                onCheckedChange={() => handleToggle(option)}
                aria-label={option}
              />
              <span>{option}</span>
            </label>
          );
        })}
        {deletedOptions.map((option) => {
          const optionId = `${fieldId}-deleted-${option}`;
          const isSelected = value.includes(option);

          return (
            <span
              key={option}
              className="flex items-center gap-2 py-1.5 text-sm cursor-not-allowed line-through opacity-60"
              aria-disabled="true"
            >
              <Checkbox
                id={optionId}
                checked={isSelected}
                disabled
                aria-label={`${option} (removed from venue configuration)`}
              />
              <span>{option}</span>
            </span>
          );
        })}
      </div>
    </fieldset>
  );
};
